import {
  CheckSquareOutlined,
  FileTextOutlined,
  KeyOutlined,
  ProjectOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Card, Empty, Input, List, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { envApiUrl } from '../../fetchHooks/envApiUrl';

const { Search } = Input;
const { Text, Title } = Typography;

interface SearchResult {
  hits: any[];
  estimatedTotalHits: number;
  processingTimeMs: number;
}

interface SearchResponse {
  query: string;
  results: {
    users: SearchResult;
    mydays: SearchResult;
    projects: SearchResult;
    tasks: SearchResult;
    credentials: SearchResult;
  };
  totalResults: number;
}

interface SearchBarProps {
  onResultSelect?: (result: any, type: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

const SearchBar: React.FC<SearchBarProps> = ({ onResultSelect, placeholder, style }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategoryExpansion = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const search = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults(null);
      setShowResults(false);
      setExpandedCategories(new Set()); // Reset expanded categories
      return;
    }

    setLoading(true);
    try {
      // Use traditional search by default, AI can be enabled with &ai=true
      const response = await fetch(`${envApiUrl}/search/search?q=${encodeURIComponent(searchQuery)}&ai=true`, {
        credentials: 'include', // Include cookies for authentication
      });
      const data = await response.json();
      setResults(data);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setQuery(value);
    search(value);
  };

  const handleResultClick = (result: any, type: string) => {
    if (onResultSelect) {
      onResultSelect(result, type);
    }
    setShowResults(false);
  };

  // Utility function to strip HTML tags
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'users':
        return <UserOutlined />;
      case 'mydays':
        return <FileTextOutlined />;
      case 'projects':
        return <ProjectOutlined />;
      case 'tasks':
        return <CheckSquareOutlined />;
      case 'credentials':
        return <KeyOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };

  const getResultNote = (result: any, type: string) => {
    switch (type) {
      case 'users':
        return `${result.firstname} ${result.lastname}`;
      case 'mydays': {
        const note = result.note;
        if (note) {
          return stripHtmlTags(note);
        }
        return t('search.untitled');
      }
      case 'projects':
        return result.name || t('search.untitledProject');
      case 'tasks':
        return result.title || t('search.untitledTask');
      case 'credentials':
        return result.website || t('search.untitledCredential');
      default:
        return result.title || result.name || t('search.untitled');
    }
  };

  const getResultDescription = (result: any, type: string) => {
    switch (type) {
      case 'users':
        return result.email || result.username;
      case 'mydays': {
        const content = result.description || result.content;
        const typeText = result.type ? `[${t(`search.types.${result.type}`)}] ` : '';
        if (content) {
          const strippedContent = stripHtmlTags(content);
          return typeText + strippedContent.substring(0, 100) + (strippedContent.length > 100 ? '...' : '');
        }
        return typeText + t('search.noDescription');
      }
      case 'projects':
        return result.description || t('search.noDescription');
      case 'tasks':
        return result.description || t('search.noDescription');
      case 'credentials': {
        const comment = result.username || result.comment;
        if (comment) {
          const strippedComment = stripHtmlTags(comment);
          return strippedComment.substring(0, 50) + (strippedComment.length > 50 ? '...' : '');
        }
        return t('search.noDescription');
      }
      default:
        return result.description || '';
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.search-container')) {
        setShowResults(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="search-container" style={{ position: 'relative', ...style }}>
      <Search
        placeholder={placeholder || t('search.placeholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onSearch={handleSearch}
        loading={loading}
        enterButton={<SearchOutlined />}
        size="large"
        style={{ width: '100%' }}
      />

      {showResults && results && (
        <Card
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '400px',
            overflowY: 'auto',
            marginTop: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin />
            </div>
          ) : results.totalResults === 0 ? (
            <Empty description={t('search.noResultsFound')} />
          ) : (
            <div>
              <div style={{ marginBottom: '12px', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Text strong>{t('search.foundResults', { count: results.totalResults, query: results.query })}</Text>
              </div>

              {Object.entries(results.results).map(([type, result]) => {
                if (result.hits.length === 0) return null;

                const displayType = type === 'mydays' ? 'lifelogs' : type;

                return (
                  <div key={type} style={{ marginBottom: '16px' }}>
                    <Title level={5} style={{ margin: '0 0 8px 0', color: '#1890ff' }}>
                      {getIcon(type)} {displayType.charAt(0).toUpperCase() + displayType.slice(1)} (
                      {result.estimatedTotalHits})
                    </Title>

                    <List
                      size="small"
                      dataSource={expandedCategories.has(type) ? result.hits : result.hits.slice(0, 3)} // Show all results if expanded, otherwise max 3
                      renderItem={(item) => (
                        <List.Item
                          style={{
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onClick={() => handleResultClick(item, type)}
                        >
                          <List.Item.Meta
                            title={
                              <Text strong style={{ fontSize: '14px' }}>
                                {getResultDescription(item, type)}
                              </Text>
                            }
                            description={
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {getResultNote(item, type)}
                              </Text>
                            }
                          />
                        </List.Item>
                      )}
                    />

                    {result.estimatedTotalHits > 3 && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: '12px',
                          marginLeft: '24px',
                          cursor: 'pointer',
                          color: '#1890ff',
                          textDecoration: 'underline',
                        }}
                        onClick={() => toggleCategoryExpansion(type)}
                      >
                        {expandedCategories.has(type)
                          ? t('search.showLess')
                          : t('search.moreResults', { count: result.estimatedTotalHits - 3 })}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default SearchBar;
